import type { Tables } from "@/integrations/supabase/types";
import type { ServiceContext } from "@prolarus/shared";
import { apiPut } from "@/lib/apiClient";
import { tryGet } from "./_shared";

type Gdpr = Tables<"gdpr">;

export interface GdprData {
  task_main_contact_complete?: boolean;
  task_dpo_complete?: boolean;
  task_eu_rep_complete?: boolean;
  task_privacy_policy_complete?: boolean;
  task_nda_complete?: boolean;
  task_contract_clauses_complete?: boolean;
  main_contact?: string | null;
  dpo_name?: string | null;
  dpo_email?: string | null;
  eu_rep_name?: string | null;
  eu_rep_email?: string | null;
  eu_rep_phone?: string | null;
  eu_rep_address1?: string | null;
  eu_rep_address2?: string | null;
  eu_rep_address3?: string | null;
  eu_rep_city?: string | null;
  eu_rep_state?: string | null;
  eu_rep_country?: string | null;
}

interface UpsertEnvelope {
  data: Gdpr | null;
  error: Error | null;
  changesLogged: boolean;
}

export const gdprService = {
  async getByCompanyId(_companyId: string) {
    const result = await tryGet<Gdpr>("/api/gdpr");
    // Existing service returns null+null when no row exists; mirror that.
    if (result.error && result.error.message?.includes("PGRST116")) {
      return { data: null, error: null };
    }
    return result;
  },

  async create(data: GdprData, _context: ServiceContext): Promise<UpsertEnvelope> {
    try {
      const result = await apiPut<{ data: Gdpr; changesLogged?: boolean }>("/api/gdpr", data);
      return { data: result.data, error: null, changesLogged: !!result.changesLogged };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err : new Error(String(err)), changesLogged: false };
    }
  },

  async update(
    _id: string,
    data: GdprData,
    _snapshot: Record<string, unknown>,
    _context: ServiceContext,
  ): Promise<UpsertEnvelope> {
    try {
      const result = await apiPut<{ data: Gdpr; changesLogged?: boolean }>("/api/gdpr", data);
      return { data: result.data, error: null, changesLogged: !!result.changesLogged };
    } catch (err) {
      return { data: null, error: err instanceof Error ? err : new Error(String(err)), changesLogged: false };
    }
  },

  async upsert(
    data: GdprData,
    _existingRecord: Gdpr | null,
    context: ServiceContext,
  ): Promise<UpsertEnvelope> {
    return gdprService.create(data, context);
  },

  async updateTaskStatus(
    taskField: keyof Pick<
      GdprData,
      "task_main_contact_complete"
      | "task_dpo_complete"
      | "task_eu_rep_complete"
      | "task_privacy_policy_complete"
      | "task_nda_complete"
      | "task_contract_clauses_complete"
    >,
    isComplete: boolean,
    _existingRecord: Gdpr | null,
    context: ServiceContext,
  ): Promise<UpsertEnvelope> {
    return gdprService.create({ [taskField]: isComplete } as GdprData, context);
  },

  async updateDpoInfo(
    dpoName: string | null,
    dpoEmail: string | null,
    _existingRecord: Gdpr | null,
    context: ServiceContext,
  ): Promise<UpsertEnvelope> {
    return gdprService.create({ dpo_name: dpoName, dpo_email: dpoEmail }, context);
  },

  async updateEuRepInfo(
    euRepData: Pick<
      GdprData,
      "eu_rep_name"
      | "eu_rep_email"
      | "eu_rep_phone"
      | "eu_rep_address1"
      | "eu_rep_address2"
      | "eu_rep_address3"
      | "eu_rep_city"
      | "eu_rep_state"
      | "eu_rep_country"
    >,
    _existingRecord: Gdpr | null,
    context: ServiceContext,
  ): Promise<UpsertEnvelope> {
    return gdprService.create(euRepData, context);
  },
};
